import { useMemo, useState } from 'react';
import { isSameDay } from 'date-fns';
import { useLang } from 'hooks/lang';

import { loaders, ClosureFormData, actions, projectStatusConfig } from 'entities';

import { Button, Column, Row } from 'components/Blocks';
import { CheckIcon, ChevronLeftIcon, ChevronRightIcon } from 'components/Icons';

import { Services } from './Services';
import { Complementary } from './Complementary';
import { LoadingForm } from './Loading';

import { useProjectProfile } from '../context';
import { useFormControl } from 'hooks';
import { useJustification } from 'providers/Justification';

import { pick } from 'utils/object';
import { validateCanFinish, validateServicesAreFilled } from './validate';

import { buildFormData } from './utils';
import { formatCurrency } from 'utils/text';

type Phase = '1' | '2';

const phaseComponents: Record<Phase, typeof Services> = {
  '1': Services,

  '2': Complementary,
};

export function FinishProject() {
  const { t } = useLang();
  const { project, setTab, updateProject } = useProjectProfile();

  const disabled = project.status === projectStatusConfig.done.code;

  const { dispatch, state } = useFormControl<ClosureFormData>(buildFormData(project));

  const { errorOrLoading } = loaders.projects.closureForm.use({
    blockLoad: !disabled,

    params: { projectId: project.id },

    onDataChange: dispatch,
  });

  const [phase, setPhase] = useState<Phase>('1');

  const Component = useMemo(() => phaseComponents[phase], [phase]);

  const canFinish = validateCanFinish(project, state);

  const isFinalPhase = phase === '2';

  const { openJustificationModal } = useJustification();

  const finishProject = actions.projects.finishProject.use({
    block: !canFinish,

    onSuccess: () => {
      updateProject((old) => ({
        ...old,

        status: projectStatusConfig.done.code,
      }));

      setTab('execution');
    },
  });

  const executeFinish = async (justification?: string): Promise<void> => {
    await finishProject.execute({
      project: project.id,

      closureForm: pick(state, [
        'comment',
        'canceledTasksPriceCheckControl',
        'emailChainAddition',
        'outsourceEmailControl',
        'poNumber',
        'servicesActualMetrics',
      ]),

      justification,
    });
  };

  const handleFinish = (): void | Promise<void> => {
    if (!canFinish) return;

    const finishingOnExpectedDate = isSameDay(new Date(), new Date(project.expectedDate as string));

    if (finishingOnExpectedDate) return executeFinish();

    openJustificationModal({
      text: t('early-done-change'),
      onConfirm: executeFinish,
    });
  };

  return (
    <Column className="pb-6">
      <Row>
        <h6>{t('send-final-costs')}</h6>

        <Row gap="2" className="rounded-sm border p-1">
          <span className="text-sm font-semibold">{t('min-fee')}</span>

          <span className="text-sm">
            {project?.minFee?.value
              ? formatCurrency(project?.minFee.value, project.currency)
              : 'n/a'}
          </span>
        </Row>
      </Row>

      {errorOrLoading ? (
        <LoadingForm />
      ) : (
        <>
          <Component state={state} dispatch={dispatch} disabled={disabled} />

          <Row justifyContent="space-between">
            {isFinalPhase && (
              <Button variant="outlined" onClick={() => setPhase('1')}>
                <ChevronLeftIcon />
                {t('back')}
              </Button>
            )}

            {isFinalPhase && disabled ? null : (
              <Button
                onClick={isFinalPhase ? handleFinish : () => setPhase('2')}
                className="ml-auto"
                disabled={
                  isFinalPhase
                    ? !canFinish
                    : !validateServicesAreFilled(project, state.servicesActualMetrics)
                }
              >
                {isFinalPhase ? t('set-project-as-done') : t('next')}
                {isFinalPhase ? <CheckIcon /> : <ChevronRightIcon />}
              </Button>
            )}
          </Row>
        </>
      )}
    </Column>
  );
}
